// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ShopPackageEntry} from '@app/features/shop/utils/ShopEntries';
import {priceCurrency, shownCents, unsoldPrice} from '@app/features/shop/utils/ShopPriceFormat';
import type {RecurringQuote} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';

// What a package's month saves against the one month package, in whole percent rounded down, from the prices on sale
// now (a running campaign counts on either side). Null for the one month package itself, without one, for a package
// without a price, one this viewer is not sold or priced in another currency, or when nothing is saved.
export function planSavingsPercent(
	entry: ShopPackageEntry,
	entries: ReadonlyArray<ShopPackageEntry>,
	now: number,
): number | null {
	if (entry.months === 1) return null;
	const month = entries.find((candidate) => candidate.months === 1);
	if (!month || unsoldPrice(month.price) || unsoldPrice(entry.price)) return null;
	if (priceCurrency(month.price) !== priceCurrency(entry.price)) return null;
	return savingsPercent(entry.months, shownCents(month.price, now), shownCents(entry.price, now));
}

// The same against the renewing month, from the server's payable amounts only.
export function recurringSavingsPercent(
	months: 1 | 3 | 12,
	quotes: ReadonlyMap<string, RecurringQuote>,
): number | null {
	const month = quotes.get('premium:1')?.payable;
	const plan = quotes.get(`premium:${months}`)?.payable;
	if (months === 1 || !month || !plan || month.currency !== plan.currency) return null;
	return savingsPercent(months, month.amount_minor, plan.amount_minor);
}

function savingsPercent(months: number, monthMinor: number, minor: number): number | null {
	if (monthMinor <= 0 || minor <= 0) return null;
	// Rounded down: a paid plan never reads 100%, and no saving is promised beyond the prices.
	const whole = months * monthMinor;
	const percent = Math.floor(((whole - minor) * 100) / whole);
	return percent > 0 ? percent : null;
}
